import { useEffect, useState } from 'react'
import { getInPlaceSettings } from '../api/in-place.js'
import { editOnSite, type EditOnSite as EditDecision, type InPlaceSettings } from '../domain/in-place-edit.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { Dialog, type MenuEntry } from '../ui/overlays.js'
import { Button, ExternalButton, LinkButton } from '../ui/primitives.js'

/**
 * The site setting, read once per site and shared by every button on screen:
 * a list with fifty rows must not ask fifty times. A failed read is "unknown"
 * (the button waits), never "off": it must not tell somebody to change a
 * setting that is perfectly fine.
 */
const known = new Map<string, InPlaceSettings>()
const asked = new Map<string, Promise<InPlaceSettings | null>>()

function readSettings(siteId: string): Promise<InPlaceSettings | null> {
  let pending = asked.get(siteId)
  if (!pending) {
    pending = getInPlaceSettings(siteId).then((settings) => {
      known.set(siteId, settings)
      return settings
    }, () => null)
    asked.set(siteId, pending)
    // Asked again the next time the screen is opened: the setting may have changed meanwhile.
    void pending.then(() => window.setTimeout(() => asked.delete(siteId), 30_000))
  }
  return pending
}

export function useInPlaceSettings(siteId: string): InPlaceSettings | null {
  const [settings, setSettings] = useState<InPlaceSettings | null>(known.get(siteId) ?? null)
  useEffect(() => {
    let alive = true
    setSettings(known.get(siteId) ?? null)
    void readSettings(siteId).then((value) => { if (alive && value) setSettings(value) })
    return () => { alive = false }
  }, [siteId])
  return settings
}

/** Forgets what was read: the settings card calls it after a save so the next button sees the new value. */
export function forgetInPlaceSettings(siteId: string): void {
  known.delete(siteId)
  asked.delete(siteId)
}

export function useEditOnSite(input: {
  siteId: string
  path: string | null
  online: boolean
  primaryDomain: string | null | undefined
}): EditDecision {
  const settings = useInPlaceSettings(input.siteId)
  return editOnSite({ settings, path: input.path, online: input.online, primaryDomain: input.primaryDomain })
}

/**
 * The same action as a row of a "more actions" menu. When it cannot open the
 * page it stays visible and disabled with the reason underneath, because a
 * missing entry would leave nobody knowing the option exists.
 */
export function editEntry(t: (key: string) => string, decision: EditDecision): MenuEntry {
  if (decision.kind === 'ready') return { key: 'edit-on-site', label: t('inplace.editOnSite'), icon: 'edit', href: decision.url }
  const why = decision.kind === 'off' ? 'inplace.list.off' : decision.kind === 'not-online' ? 'inplace.list.notOnline' : 'inplace.list.noAddress'
  return { key: 'edit-on-site', label: t('inplace.editOnSite'), icon: 'edit', disabled: true, secondary: t(why) }
}

/** Why the button cannot open the page yet, and what to do: one sentence per reason. */
export function EditOnSiteExplanation({ decision, siteId, onClose }: { decision: EditDecision; siteId: string; onClose: () => void }) {
  const { t } = useI18n()
  const key = decision.kind === 'off' ? 'off' : decision.kind === 'not-online' ? 'notOnline' : 'noAddress'
  return <Dialog
    title={t('inplace.explain.title')}
    icon="edit"
    onClose={onClose}
    actions={<>
      <Button variant="text" onClick={onClose}>{t('common.close')}</Button>
      {decision.kind === 'off' && <LinkButton variant="filled" to={paths.siteSection(siteId, 'impostazioni')} onClick={onClose}>{t('inplace.explain.openSettings')}</LinkButton>}
    </>}
  >
    <p>{t(`inplace.explain.${key}`)}</p>
  </Dialog>
}

/**
 * «Modifica sul sito»: opens the published address with `#cms-edit`. When it
 * cannot, it is still a button, and pressing it explains what to do instead of
 * leaving a disabled control nobody can interrogate.
 */
export function EditOnSiteButton({ siteId, path, online, primaryDomain, variant = 'outlined' }: {
  siteId: string
  path: string | null
  online: boolean
  primaryDomain: string | null | undefined
  variant?: 'outlined' | 'text' | 'filled'
}) {
  const { t } = useI18n()
  const decision = useEditOnSite({ siteId, path, online, primaryDomain })
  const [explaining, setExplaining] = useState(false)
  if (decision.kind === 'ready') {
    return <ExternalButton variant={variant} icon="edit" href={decision.url}>{t('inplace.editOnSite')}</ExternalButton>
  }
  if (decision.kind === 'loading') return null
  return <>
    <Button variant={variant} icon="edit" onClick={() => setExplaining(true)}>{t('inplace.editOnSite')}</Button>
    {explaining && <EditOnSiteExplanation decision={decision} siteId={siteId} onClose={() => setExplaining(false)}/>}
  </>
}
